import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, useRef } from 'react';
import { CardInfo } from '../../../../../components/TeachingBlockGrid/CardInfo/CardInfo';
import { useTeachingBlockStore } from '../../../../../components/TeachingBlockGrid/Contexts/TeachingBlockContext';
import { GridActionArea } from '../../../../../components/TeachingBlockGrid/GridActionArea/GridActionArea';
import { GridMenuBar } from '../../../../../components/TeachingBlockGrid/GridMenuBar/GridMenuBar';
import { PreviewMap } from '../../../../../components/TeachingBlockGrid/PreviewMap/PreviewMap';
import { TeachingBlockGrid } from '../../../../../components/TeachingBlockGrid/TeachingBlockGrid';
import styles from './TeachingBlockVersionBoard.module.scss';

export type TeachingBlockVersionBoardViews = 'classes' | 'teachers';

export const TeachingBlockVersionBoard = observer(() => {
  const store = useTeachingBlockStore();
  const isSplit = store.boardLayout === 'split';

  // Regler 1: zwischen Auswahl-Bereich und Board (Container = board-body).
  // split → Breite (%) über X, stacked → Höhe (px) über Y.
  const containerRef = useRef<HTMLDivElement>(null);
  const startResizeBoard = (event: ReactMouseEvent) => {
    event.preventDefault();
    const onMove = (move: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (isSplit) {
        store.setBoardSplitWidth(((move.clientX - rect.left) / rect.width) * 100);
      } else {
        store.setBoardTopHeight(move.clientY - rect.top);
      }
    };
    attachDrag(onMove);
  };
  const onBoardDividerKeyDown = (event: ReactKeyboardEvent) => {
    if (isSplit) {
      if (event.key === 'ArrowLeft') store.setBoardSplitWidth(store.boardSplitWidth - 2);
      if (event.key === 'ArrowRight') store.setBoardSplitWidth(store.boardSplitWidth + 2);
    } else {
      if (event.key === 'ArrowUp') store.setBoardTopHeight(store.boardTopHeight - 16);
      if (event.key === 'ArrowDown') store.setBoardTopHeight(store.boardTopHeight + 16);
    }
  };

  // Regler 2: zwischen CardPicker und CardInfo (Container = panels).
  // stacked → nebeneinander, Breite (%) über X; split → übereinander, Höhe (%) über Y.
  const panelsRef = useRef<HTMLDivElement>(null);
  const startResizePanels = (event: ReactMouseEvent) => {
    event.preventDefault();
    const onMove = (move: MouseEvent) => {
      const rect = panelsRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (isSplit) {
        store.setBoardPickerHeight(((move.clientY - rect.top) / rect.height) * 100);
      } else {
        store.setBoardPickerWidth(((move.clientX - rect.left) / rect.width) * 100);
      }
    };
    attachDrag(onMove);
  };
  const onPanelsDividerKeyDown = (event: ReactKeyboardEvent) => {
    if (isSplit) {
      if (event.key === 'ArrowUp') store.setBoardPickerHeight(store.boardPickerHeight - 2);
      if (event.key === 'ArrowDown') store.setBoardPickerHeight(store.boardPickerHeight + 2);
    } else {
      if (event.key === 'ArrowLeft') store.setBoardPickerWidth(store.boardPickerWidth - 2);
      if (event.key === 'ArrowRight') store.setBoardPickerWidth(store.boardPickerWidth + 2);
    }
  };

  // Regler 3: zwischen (optionaler) PreviewMap und CardInfo. Größe der Map als % des Rest-Bereichs
  // (hinter dem Picker), gemessen ab dessen Kante — CardInfo füllt den verbleibenden Rest.
  const restRef = useRef<HTMLDivElement>(null);
  const previewMapRef = useRef<HTMLDivElement>(null);
  const startResizePreviewMap = (event: ReactMouseEvent) => {
    event.preventDefault();
    const onMove = (move: MouseEvent) => {
      const restRect = restRef.current?.getBoundingClientRect();
      if (!restRect) return;
      if (isSplit) {
        store.setBoardPreviewMapHeight(((move.clientY - restRect.top) / restRect.height) * 100);
      } else {
        store.setBoardPreviewMapWidth(((move.clientX - restRect.left) / restRect.width) * 100);
      }
    };
    attachDrag(onMove);
  };
  const onPreviewMapDividerKeyDown = (event: ReactKeyboardEvent) => {
    if (isSplit) {
      if (event.key === 'ArrowUp') store.setBoardPreviewMapHeight(store.boardPreviewMapHeight - 2);
      if (event.key === 'ArrowDown') store.setBoardPreviewMapHeight(store.boardPreviewMapHeight + 2);
    } else {
      if (event.key === 'ArrowLeft') store.setBoardPreviewMapWidth(store.boardPreviewMapWidth - 2);
      if (event.key === 'ArrowRight') store.setBoardPreviewMapWidth(store.boardPreviewMapWidth + 2);
    }
  };

  return (
    <div className='board-wrapper' id='teaching-block-grid'>
      <div className={styles.surface}>
        <div className={styles['menu-section']}>
          <GridMenuBar />
        </div>
        <div ref={containerRef} className={classNames(styles['board-body'], styles[isSplit ? 'split' : 'stacked'])}>
          <div
            ref={panelsRef}
            className={styles.panels}
            style={isSplit ? { width: `${store.boardSplitWidth}%` } : { height: `${store.boardTopHeight}px` }}
          >
            <div
              className={styles['picker-panel']}
              style={isSplit ? { height: `${store.boardPickerHeight}%` } : { width: `${store.boardPickerWidth}%` }}
              data-card-stack=''
            >
              <GridActionArea />
            </div>
            <div
              className={classNames(styles.divider, styles[isSplit ? 'horizontal' : 'vertical'])}
              role='separator'
              aria-orientation={isSplit ? 'horizontal' : 'vertical'}
              tabIndex={0}
              onMouseDown={startResizePanels}
              onKeyDown={onPanelsDividerKeyDown}
            />
            {/* Rest-Bereich hinter dem Picker: optionale PreviewMap (feste Größe) + CardInfo (füllt
                den Rest). So kann die Map die CardInfo nie verdecken – beide teilen sich den Platz. */}
            <div ref={restRef} className={styles.rest}>
              {store.showPreviewMap && (
                <>
                  <div
                    ref={previewMapRef}
                    className={styles['preview-map-panel']}
                    style={
                      isSplit
                        ? { height: `${store.boardPreviewMapHeight}%` }
                        : { width: `${store.boardPreviewMapWidth}%` }
                    }
                  >
                    <PreviewMap />
                  </div>
                  <div
                    className={classNames(styles.divider, styles[isSplit ? 'horizontal' : 'vertical'])}
                    role='separator'
                    aria-orientation={isSplit ? 'horizontal' : 'vertical'}
                    tabIndex={0}
                    onMouseDown={startResizePreviewMap}
                    onKeyDown={onPreviewMapDividerKeyDown}
                  />
                </>
              )}
              <div className={styles['info-panel']}>
                <CardInfo />
              </div>
            </div>
          </div>
          <div
            className={classNames(styles.divider, styles[isSplit ? 'vertical' : 'horizontal'])}
            role='separator'
            aria-orientation={isSplit ? 'vertical' : 'horizontal'}
            tabIndex={0}
            onMouseDown={startResizeBoard}
            onKeyDown={onBoardDividerKeyDown}
          />
          <div className={styles['board-panel']}>
            <TeachingBlockGrid />
          </div>
        </div>
      </div>
    </div>
  );
});

TeachingBlockVersionBoard.displayName = 'TeachingBlockVersionBoard';

// Gemeinsame Drag-Mechanik: mousemove/mouseup am document, Textauswahl während des Ziehens aus.
function attachDrag(onMove: (event: MouseEvent) => void) {
  const onUp = () => {
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
}
